import type { CompositionPageScope } from '@revelio/core/global-ai-assistant/composition-page-scope';
import type { AssistantDiscoveryKind } from '@revelio/core/global-ai-assistant/discovery-targets';
import type { TurnStepEvent as SharedTurnStepEvent } from '@revelio/core/global-ai-assistant/turn-step-event';

import { dashMetaFetch } from '../../shared/api/dash-meta-fetch';
import { consumeSse } from './global-ai-run-page-stream';
import { turnStepEventSchema } from './global-ai-turn-step-event.schema';

export type TurnToolCall = { id: string; name: string; argumentsJson: string };

export type TurnMessage =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string }
  | { role: 'assistant'; content?: string; toolCalls: TurnToolCall[] }
  | { role: 'tool'; toolCallId: string; content: string };

export type TurnStepRequestBody = {
  messages: TurnMessage[];
  stepIndex: number;
  context?: {
    conversationSummary?: string;
    rolesTax?: string;
    skillsTax?: number;
    /**
     * The user's taxonomy preference is ADAPTIVE: `rolesTax`/`skillsTax` are
     * then only the stored last-resolved fallback, and the server must
     * re-derive the taxonomy from the prompt's own company context (same
     * contract as the standalone ai-search endpoint's `roles_tax_adaptive`).
     */
    rolesTaxAdaptive?: boolean;
    skillsTaxAdaptive?: boolean;
    activitiesTax?: number;
    activitiesTaxAdaptive?: boolean;
    /**
     * The account has the activity filter. Sent only when
     * definitively true — never `false`/`null` while Unleash is loading, so a
     * still-loading flag is never read server-side as a confirmed capability.
     */
    activitiesEnabled?: boolean;
    /** Per-page rollout flags for explicit cross-page filter targets. */
    activityFilterFlags?: { companyPages: boolean; otherPages: boolean };
    /**
     * Geography taxonomy id (metro 1 / admin 2) from the user's GLOBAL
     * geography preference. When absent, the server resolves geography
     * under metro.
     */
    geoTax?: number;
    /**
     * True when the account is on a trial. Export/download actions are disabled
     * for these users, so a usage answer describing them must say the feature
     * is not in their plan rather than give steps they cannot follow. Sent only
     * when definitively true — never `false`/`null` while the flag is loading.
     */
    isTrialUser?: boolean;
    currentPage?: {
      pageKind?: string;
      canFilter?: boolean;
      discoveryKind?: AssistantDiscoveryKind;
      matchedOptionId?: string;
      baseFilterPrompt?: string;
      /**
       * The page's ACTIVE filters as a {@link CompositionPageScope}, built by
       * `buildCurrentPageScope`. Sent only when the page can filter. Its
       * `omittedFilterNames` are HUMAN-READABLE ("Employment Type", "School")
       * — never
       * `FilterId`s — because the read repeats them verbatim in a note the
       * model is told to relay.
       */
      scope?: CompositionPageScope;
    };
    customDash?: {
      inCustomDashEditMode?: boolean;
      hasCurrentPageItems?: boolean;
    };
    /**
     * `__std__:` navigate option ids the account cannot access. The backend
     * strips these from the navigate candidate enum and pages grid so the model
     * never proposes or calls an inaccessible page.
     */
    deniedNavigateOptionIds?: string[];
  };
};

export type TurnStepEvent = SharedTurnStepEvent;

export async function runTurnStep(params: {
  body: TurnStepRequestBody;
  onEvent: (event: TurnStepEvent) => void;
  onInvalidEnvelope?: (raw: string, reason: 'json' | 'schema') => void;
  signal?: AbortSignal;
}): Promise<void> {
  const response = await dashMetaFetch(
    '/api/build-a-dash/ai-gen/global-assistant/turn-step',
    {
      method: 'POST',
      body: JSON.stringify(params.body),
      signal: params.signal,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  // Every server step terminates with a `done` or `error` event. If the stream
  // ends without one (and we didn't abort it), the connection was cut mid-step
  // — surface it as an error so the loop ends the turn honestly instead of
  // reading the partial output as a complete, successful answer.
  let sawTerminal = false;
  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) return;
      const raw = line.slice('data: '.length);
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        params.onInvalidEnvelope?.(raw, 'json');
        return;
      }
      const result = turnStepEventSchema.safeParse(parsed);
      if (!result.success) {
        params.onInvalidEnvelope?.(raw, 'schema');
        return;
      }
      const event = result.data;
      if (event.type === 'done' || event.type === 'error') {
        sawTerminal = true;
      }
      params.onEvent(event);
    },
    params.signal
  );
  if (!params.signal?.aborted && !sawTerminal) {
    params.onEvent({
      type: 'error',
      content: 'The response was cut off before it finished. Please try again.',
    });
  }
}
